import { SearchTypeTabs } from '@/components/search/search-type-tabs';
import { AiBadge, SmartSearchForm } from '@/components/search/smart-search-form';
import { Button } from '@/components/ui/button';
import { DEFAULT_SMART_SEARCH_SETTINGS, SEARCH_TYPE_TITLES, trackSearchEvent } from '@/lib/smart-search';
import { cn } from '@/lib/utils';
import { type SharedData } from '@/types';
import { type SearchType } from '@/types/search-criteria';
import { router, usePage } from '@inertiajs/react';
import { Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Pages where the big search is already on the screen: no "Buscar" button and no magnifier (spec 5.2 / 5.3). */
const PAGES_WITH_OWN_SEARCH = new Set(['welcome', 'results']);

/**
 * Initial tab of the top / mobile search by page (spec 5.3): company page, company stock and company results open
 * on "Empresas"; listing, category, geo and every other page open on "Embarcações".
 */
export const initialSearchTypeFor = (component: string, props: Record<string, unknown>): SearchType => {
    if (component.startsWith('listings/') || component.startsWith('servicos')) {
        return 'empresas';
    }

    if (component === 'results') {
        const categorySeo = props.categorySeo as { page_kind?: string } | null | undefined;

        if (props.type === 'empresas' || categorySeo?.page_kind === 'listing_stock') {
            return 'empresas';
        }
    }

    return 'embarcacoes';
};

interface SiteSearchOverlayProps {
    /** "button" renders the compact "Buscar" + IA badge of the desktop top bar; "icon" the magnifier of the phone. */
    trigger: 'button' | 'icon';
    className?: string;
}

/**
 * Search in every public page (CN-200, 5.2 and 5.3). Desktop: a compact "Buscar" button next to the menus that opens the
 * search over the top bar (full width, dimmed page; closes with X, click outside and Esc). Phone: a magnifier that opens
 * a full-screen search. Both carry the "Embarcações | Empresas" tabs, example phrases, shortcuts and the wrong-tab hint.
 */
export function SiteSearchOverlay({ trigger, className }: SiteSearchOverlayProps) {
    const page = usePage<SharedData>();
    const settings = page.props.smartSearch ?? DEFAULT_SMART_SEARCH_SETTINGS;
    const hidden = PAGES_WITH_OWN_SEARCH.has(page.component);
    const [isOpen, setIsOpen] = useState(false);
    const [type, setType] = useState<SearchType>(() => initialSearchTypeFor(page.component, page.props));
    const panelRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => {
        setIsOpen(false);
        triggerRef.current?.focus();
    }, []);

    const open = () => {
        setType(initialSearchTypeFor(page.component, page.props));
        setIsOpen(true);
        trackSearchEvent('topo_busca_aberta', initialSearchTypeFor(page.component, page.props), { origem: trigger === 'icon' ? 'lupa' : 'topo' });
    };

    // Esc closes; the page behind does not scroll while the search is open.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [close, isOpen]);

    // A navigation (a search sent, a shortcut clicked) closes the overlay.
    useEffect(() => {
        return router.on('navigate', () => setIsOpen(false));
    }, []);

    if (hidden) {
        return null;
    }

    const handleTypeChange = (next: SearchType) => {
        if (next !== type) {
            trackSearchEvent('busca_aba_trocada', next, { origem: trigger === 'icon' ? 'lupa' : 'topo' });
        }

        setType(next);
    };

    const origin = trigger === 'icon' ? 'lupa' : 'topo';

    return (
        <>
            {trigger === 'button' ? (
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={open}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    className={cn(
                        'inline-flex h-10 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 transition-colors hover:border-gray-400 hover:bg-gray-50',
                        className,
                    )}
                >
                    <Search aria-hidden="true" className="h-4 w-4" />
                    Buscar
                    <AiBadge />
                </button>
            ) : (
                <Button ref={triggerRef} type="button" variant="ghost" size="sm" onClick={open} aria-haspopup="dialog" aria-expanded={isOpen} className={className}>
                    <Search className="h-6 w-6" />
                    <span className="sr-only">Buscar</span>
                </Button>
            )}

            {isOpen && (
                <div
                    className="fixed inset-0 z-[60] bg-black/50"
                    onMouseDown={(event) => {
                        if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                            close();
                        }
                    }}
                >
                    <div
                        ref={panelRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="site-search-title"
                        className={cn(
                            'flex flex-col bg-white shadow-xl',
                            trigger === 'icon' ? 'h-[100dvh] w-full overflow-y-auto' : 'max-h-[100dvh] w-full overflow-y-auto border-b border-gray-200',
                        )}
                    >
                        <div className="mx-auto w-full max-w-5xl px-4 py-4 sm:px-6 sm:py-6">
                            <div className="mb-4 flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <SearchTypeTabs value={type} onChange={handleTypeChange} idPrefix="site-search" />
                                </div>
                                <Button type="button" variant="ghost" size="icon" onClick={close} aria-label="Fechar busca" className="shrink-0">
                                    <X aria-hidden="true" className="h-5 w-5" />
                                </Button>
                            </div>

                            <div id={`site-search-panel-${type}`} role="tabpanel" aria-labelledby={`site-search-tab-${type}`} className="space-y-4">
                                <h2 id="site-search-title" className="flex items-center gap-2 text-lg font-semibold text-gray-900 sm:text-xl">
                                    {SEARCH_TYPE_TITLES[type]}
                                    {settings.enabled[type] && <AiBadge />}
                                </h2>

                                <SmartSearchForm
                                    key={type}
                                    type={type}
                                    origin={origin}
                                    autoFocus
                                    showExamples
                                    showShortcuts
                                    showPreferFilters
                                    onSwitchType={handleTypeChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
